import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { ApiService } from 'src/app/core/services/api.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { PlaceStatus } from 'src/app/shared/enums/place-status.enum';
import { SaleOrderType } from 'src/app/shared/enums/sale-order-type.enum';
import { environment } from 'src/environments/environment';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { IPaginatorProperties } from '../../../core/models/paginator.interface';
import { IShopBase, IShopFilter } from '../../../core/models/shop.interface';
import { ISaleOrder } from '../models/sale-order.interface';
import { PDFDocument } from 'pdf-lib';

@Injectable()
export class SaleOrderService extends ApiService {

    public fileLoadedObserver = new Subject<any>();
    public unselectCheckbox = new BehaviorSubject<any>(null);

    constructor(
        public http: HttpClient,
        public storageBrowser: LocalStorageService,
        private notify: NotificationService
    ) {
        super(http, storageBrowser);
    }

    /**
     * Angular 11 collapses arrays in `{ params }` into comma-joined scalars — Express then sends a single string.
     * Use repeated keys so `marketplaces`, `ShopId`, `statuses`, etc. round-trip as arrays.
     */
    private appendQueryKv(params: HttpParams, key: string, value: unknown): HttpParams {
        if (value === undefined || value === null) return params;
        if (Array.isArray(value)) {
            let next = params;
            for (const item of value) {
                if (item === undefined || item === null || item === '') continue;
                next = next.append(key, String(item));
            }
            return next;
        }
        return params.append(key, String(value));
    }

    private saleOrderHttpParams(query: Record<string, unknown>): HttpParams {
        let p = new HttpParams();
        for (const key of Object.keys(query)) {
            p = this.appendQueryKv(p, key, query[key]);
        }
        return p;
    }

    getOrders(
        page: number = 1,
        limit: number = 100,
        q: string = '',
        companyId?: any,
        amountType?: string,
        customerCountry?: string,
        shopId?: string,
        searchParams?: any,
        orderField: string = '',
        orderDirection: string = '',
        forPayouts: boolean = false,
        isB2B: string = SaleOrderType.All.toString(),
        isIndVat?: boolean,
        placeStatus: PlaceStatus = PlaceStatus.Confirmed,
        newFilters: {
            originCountry?: string;
            destinationCountry?: string;
            dateToEmpact?: string;
            dateFromEmpact?: string;
            deliveryDateTo?: string;
            deliveryDateFrom?: string;
        } = {},
        isSelectAll?: boolean,
    ): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders`,
            query: this.saleOrderHttpParams({
                ...newFilters,
                q: q,
                limit: limit,
                page: page,
                companyId: companyId ? companyId : '',
                marketplaces: searchParams?.marketplaces,
                //customerTypes: searchParams?.companyTypes,
                ShopId: shopId,
                dateTo: searchParams?.dateTo,
                dateFrom: searchParams?.dateFrom,
                amountType: amountType,
                customerCountry: customerCountry,
                orderField: orderField,
                orderDirection: orderDirection,
                statuses: searchParams?.orderStatus,
                forPayouts: forPayouts ? forPayouts : false,
                isB2B: isB2B ? isB2B : SaleOrderType.All.toString(),
                isIndVat: isIndVat ? isIndVat : false,
                placeStatus: placeStatus,
                isSelectAll
            } as Record<string, unknown>)
        });
    }

    getOrdersCursor(
        cursor: string | null = null,
        limit: number = 50,
        q: string = '',
        companyId?: any,
        batchId?:any,
        partnerId?: any,
        fileId?: any,
        amountType?: string,
        customerCountry?: string,
        shopId?: string,
        searchParams?: any,
        orderField: string = '',
        orderDirection: string = '',
        forPayouts: boolean = false,
        isB2B: string = SaleOrderType.All.toString(),
        isIndVat?: boolean,
        placeStatus?: PlaceStatus,
        newFilters: {
            originCountry?: string;
            destinationCountry?: string;
            batchCountryOrigin?: string;
            batchCountryDestination?: string;
            dateToEmpact?: string;
            dateFromEmpact?: string;
            deliveryDateTo?: string;
            deliveryDateFrom?: string;
        } = {},
        isSelectAll? : boolean,
    ): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders/b2c-cursor`,
            query: this.saleOrderHttpParams({
                ...newFilters,
                q: q,
                limit: limit,
                cursor: cursor,
                companyId: companyId ? companyId : '',
                batchId: batchId ? batchId: '',
                batchPartnerId: partnerId ? partnerId: '',
                fileId: fileId ? fileId: '',
                marketplaces: searchParams?.marketplaces,
                customerTypes: searchParams?.companyTypes,
                ShopId: shopId,
                dateTo: searchParams?.dateTo,
                dateFrom: searchParams?.dateFrom,
                amountType: amountType,
                customerCountry: customerCountry,
                orderField: orderField,
                orderDirection: orderDirection,
                statuses: searchParams?.orderStatus,
                operationalStatuses: searchParams?.operationalStatus,
                financialStatuses: searchParams?.financialStatus,
                forPayouts: forPayouts ? forPayouts : false,
                isB2B: isB2B ? isB2B : SaleOrderType.All.toString(),
                isIndVat: isIndVat ? isIndVat : false,
                placeStatus: placeStatus,
                isSelectAll: isSelectAll
            } as Record<string, unknown>)
        });
    }

    getOrdersB2CTest(
        page: number = 1,
        limit: number = 100,
        q: string = '',
        companyId?: any,
        batchId?:any,
        partnerId?: any,
        fileId?: any,
        amountType?: string,
        customerCountry?: string,
        shopId?: string,
        searchParams?: any,
        orderField: string = '',
        orderDirection: string = '',
        forPayouts: boolean = false,
        isB2B: string = SaleOrderType.All.toString(),
        isIndVat?: boolean,
        placeStatus?: PlaceStatus,
        newFilters: {
            originCountry?: string;
            destinationCountry?: string;
            batchCountryOrigin?: string;
            batchCountryDestination?: string;
            dateToEmpact?: string;
            dateFromEmpact?: string;
            deliveryDateTo?: string;
            deliveryDateFrom?: string;
        } = {},
        isSelectAll? : boolean,
    ): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders/b2c-test`,
            query: this.saleOrderHttpParams({
                ...newFilters,
                q: q,
                limit: limit,
                page: page,
                companyId: companyId ? companyId : '',
                batchId: batchId ? batchId: '',
                batchPartnerId: partnerId ? partnerId: '',
                fileId: fileId ? fileId: '',
                marketplaces: searchParams?.marketplaces,
                customerTypes: searchParams?.companyTypes,
                ShopId: shopId,
                dateTo: searchParams?.dateTo,
                dateFrom: searchParams?.dateFrom,
                amountType: amountType,
                customerCountry: customerCountry,
                orderField: orderField,
                orderDirection: orderDirection,
                statuses: searchParams?.orderStatus,
                operationalStatuses: searchParams?.operationalStatus,
                financialStatuses: searchParams?.financialStatus,
                forPayouts: forPayouts ? forPayouts : false,
                isB2B: isB2B ? isB2B : SaleOrderType.All.toString(),
                isIndVat: isIndVat ? isIndVat : false,
                placeStatus: placeStatus,
                isSelectAll: isSelectAll
            } as Record<string, unknown>)
        });
    }

    getMarketplaceOrders(
        page: number = 1,
        limit: number = 100,
        q: string = '',
        companyId?: any,
        isSelectAll: boolean = false,
        amountType?: string,
        customerCountry?: string,
        shopId?: string,
        searchParams?: any,
        orderField: string = '',
        orderDirection: string = '',
        forPayouts: boolean = false,
        isB2B: string = SaleOrderType.All.toString(),
        isIndVat?: boolean,
        placeStatus: PlaceStatus = PlaceStatus.Confirmed,
        newFilters: {
            originCountry?: string;
            destinationCountry?: string;
            dateToEmpact?: string;
            dateFromEmpact?: string;
        } = {},
    ): Observable<any> {
        console.log('isSelectAll getMarketplaceOrders');
        console.log(isSelectAll);
        return this.request({
            method: 'GET',
            path: `sale-orders/ecommerce-sale-orders`,
            query: this.saleOrderHttpParams({
                ...newFilters,
                q: q,
                limit: limit,
                page: page,
                companyId: companyId ? companyId : '',
                marketplaces: searchParams?.marketplaces,
                customerTypes: searchParams?.companyTypes,
                ShopId: shopId,
                dateTo: searchParams?.dateTo,
                dateFrom: searchParams?.dateFrom,
                amountType: amountType,
                customerCountry: customerCountry,
                orderField: orderField,
                orderDirection: orderDirection,
                statuses: searchParams?.orderStatus,
                forPayouts: forPayouts ? forPayouts : false,
                isB2B: isB2B ? isB2B : SaleOrderType.All.toString(),
                isIndVat: isIndVat ? isIndVat : null,
                placeStatus: placeStatus,
                isSelectAll: isSelectAll
            } as Record<string, unknown>)
        });
    }

    getSalesOrderUploadHistory(
        page: number = 1,
        limit: number = 100,
        q: string = '',
        searchParams?: any,
        orderField: string = '',
        orderDirection: string = '',
    ): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sales-orders-upload-history/upload-history`,
            query: {
                q: q,
                limit: limit,
                page: page,
                dateTo: searchParams?.dateTo,
                dateFrom: searchParams?.dateFrom,
                orderField: orderField,
                orderDirection: orderDirection,
            }
        });
    }

    // B2B SO Total
    getOrdersTotalB2B(
        page: number = 1,
        limit: number = 100,
        q: string = '',
        companyId?: number,
        amountType?: string,
        customerCountry?: string,
        searchParams?: any,
        orderField: string = '',
        orderDirection: string = '',
        includingCancelled: boolean = false,
        forPayouts: boolean = false,
        isB2B: boolean = true,
        isIndVat?: boolean,
        placeStatus: PlaceStatus = PlaceStatus.Confirmed,
        newFilters: {
            originCountry?: string;
            destinationCountry?: string;
            dateToEmpact?: string;
            dateFromEmpact?: string;
        } = {},
        isEmpact: boolean = false,
    ): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders/filterB2BSOTotal`,
            query: {
                ...newFilters,
                q: q,
                limit: limit,
                page: page,
                companyId: companyId ? companyId : '',
                marketplaces: searchParams?.marketplaces,
                customerTypes: searchParams?.companyTypes,
                dateTo: searchParams?.dateTo,
                dateFrom: searchParams?.dateFrom,
                amountType: amountType,
                customerCountry: customerCountry,
                orderField: orderField,
                orderDirection: orderDirection,
                statuses: searchParams?.orderStatus,
                includingCancelled: includingCancelled ? includingCancelled : false,
                forPayouts: forPayouts ? forPayouts : false,
                isB2B: true,
                isIndVat: isIndVat ? isIndVat : false,
                placeStatus: placeStatus,
                isEmpact,
            }
        });
    }

    getOrdersTotal(
        page: number = 1,
        limit: number = 100,
        q: string = '',
        companyId?: number,
        amountType?: string,
        customerCountry?: string,
        searchParams?: any,
        orderField: string = '',
        orderDirection: string = '',
        includingCancelled: boolean = false,
        forPayouts: boolean = false,
        isB2B: string = SaleOrderType.All.toString(),
        isIndVat?: boolean,
        placeStatus: PlaceStatus = PlaceStatus.Confirmed,
        newFilters: {
            originCountry?: string;
            destinationCountry?: string;
            dateToEmpact?: string;
            dateFromEmpact?: string;
            ShopId?: any;
        } = {}
    ): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders/total`,
            query: {
                ...newFilters,
                q: q,
                limit: limit,
                page: page,
                companyId: companyId ? companyId : '',
                marketplaces: searchParams?.marketplaces,
                customerTypes: searchParams?.companyTypes,
                dateTo: searchParams?.dateTo,
                dateFrom: searchParams?.dateFrom,
                amountType: amountType,
                customerCountry: customerCountry,
                orderField: orderField,
                orderDirection: orderDirection,
                statuses: searchParams?.orderStatus,
                includingCancelled: includingCancelled ? includingCancelled : false,
                forPayouts: forPayouts ? forPayouts : false,
                isB2B: isB2B ? isB2B : SaleOrderType.All.toString(),
                isIndVat: isIndVat ? isIndVat : false,
                placeStatus: placeStatus,
            }
        });
    }

    // B2C SO total
    getB2COrdersTotal(
        page: number = 1,
        limit: number = 100,
        q: string = '',
        companyId?: number,
        amountType?: string,
        customerCountry?: string,
        searchParams?: any,
        orderField: string = '',
        orderDirection: string = '',
        // includingCancelled: boolean = false,
        forPayouts: boolean = false,
        isB2B: boolean = false,
        isIndVat: boolean = false,
    ): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders/totalB2C`,
            query: {
                q: q,
                limit: limit,
                page: page,
                companyId: companyId ? companyId : '',
                marketplaces: searchParams?.marketplaces,
                customerTypes: searchParams?.companyTypes,
                dateTo: searchParams?.dateTo,
                dateFrom: searchParams?.dateFrom,
                amountType: amountType,
                customerCountry: customerCountry,
                orderField: orderField,
                orderDirection: orderDirection,
                statuses: searchParams?.orderStatus,
                forPayouts: forPayouts ? forPayouts : false,
                isB2B: isB2B ? isB2B : SaleOrderType.All.toString(),
                isIndVat: isIndVat ? isIndVat : false,
            }
        });
    }

    // B2C SO total
    // getB2COrdersTotal(
    //     page: number = 1,
    //     limit: number = 100,
    //     q: string = '',
    //     companyId?: number,
    //     amountType?: string,
    //     customerCountry?: string,
    //     searchParams?: any,
    //     orderField: string = '',
    //     orderDirection: string = '',
    //     // includingCancelled: boolean = false,
    //     forPayouts: boolean = false,
    //     isB2B: boolean = false,
    //     isIndVat: boolean = false,
    // ): Observable<any> {
    //     return this.request({
    //         method: 'GET',
    //         path: `sale-orders/totalB2C`,
    //         query: {
    //             q: q,
    //             limit: limit,
    //             page: page,
    //             companyId: companyId ? companyId : '',
    //             marketplaces: searchParams?.marketplaces,
    //             customerTypes: searchParams?.companyTypes,
    //             dateTo: searchParams?.dateTo,
    //             dateFrom: searchParams?.dateFrom,
    //             amountType: amountType,
    //             customerCountry: customerCountry,
    //             orderField: orderField,
    //             orderDirection: orderDirection,
    //             statuses: searchParams?.orderStatus,
    //             forPayouts: forPayouts ? forPayouts : false,
    //             isB2B: isB2B ? isB2B : SaleOrderType.All.toString(),
    //             isIndVat: isIndVat ? isIndVat : null,
    //         }
    //     });
    // }

    getfileName(page: number = 1, limit: number = 20, q: string = ''): Observable<any> {
        return this.request({
            path: 'xlsx-sale-order-files/list/files',
            method: 'GET',
            query: {
                page,
                limit,
                q,
            }
        });
    }

    getOrderById(id: number): Observable<ISaleOrder> {
        return this.request({
            path: `sale-orders/${id}/details`,
            method: 'GET',
        });
    }

    getOrderByMarketPlaceId(id: string): Observable<ISaleOrder> {
        return this.request({
            path: `sale-orders/market-place/${id}`,
            method: 'GET',
        });
    }


    getSaleOrderByCompnayId(companyId: number): Observable<any> {
        return this.request({
            path: `sale-orders/get-sale-orders-by-company-id/${companyId}`,
            method: 'GET',
        });
    }

    getPartnerSOByCompnayId(companyId: number, invoiceId: any): Observable<any> {
        return this.request({
            path: `partner/get-partner-so-by-company-id/get/${companyId}/${invoiceId}`,
            method: 'GET',
        });
    }

    createOrder(body): Observable<any> {
        return this.request({
            path: `sale-orders`,
            method: 'POST',
            body: body
        });
    }

    updateDraftOrder(body): Observable<any> {
        return this.request({
            path: `sale-orders/draft-to-actual`,
            method: 'POST',
            body: body
        });
    }

    createDraftOrder(body): Observable<any> {
        return this.request({
            path: `sale-orders/draft-sale-orders`,
            method: 'POST',
            body: body
        });
    }

    changeTrackingNumber(body): Observable<any> {
        return this.request({
            path: `sale-orders/change-tracking-number`,
            method: 'POST',
            body: body
        });
    }

    createMultipleOrder(body): Observable<any> {
        return this.request({
            path: `sale-orders/create-for-all`,
            method: 'POST',
            body: body
        });
    }

    applyChanges(body): Observable<any> {
        return this.request({
            path: `sale-orders/apply-changes`,
            method: 'POST',
            body: body
        });
    }

    /**
     * get shops for dropdown infinity scroll
     * @old getAllShops
     */
    public getShops(filter: IShopFilter): Observable<IPaginatorProperties<IShopBase>> {
        return this.request({
            path: `company-marketplaces/shops/get-shops`,
            method: 'GET',
            query: filter,
        });
    }

    /**
     * bed solution select all shops by one call
     * @Deprecated
     * @new getShops
     */
    getAllShops(q, page?: number, limit?: number, companyId?: number): Observable<any> {
        return this.request({
            path: `company-marketplaces/shops/get-all`,
            method: 'GET',
            query: {
                q: q || '',
                ...(page ? { page } : {}),
                ...(limit ? { limit } : {}),
                ...(companyId ? { companyId } : {}),
            }
        }).pipe(
            map((res) => this.normalizeGetAllShopsResponse(res, page, limit))
        );
    }

    getAllShopsList(q: string = ''): Observable<any[]> {
        return this.getAllShops(q || '', 1, 10000).pipe(
            map((res) => res?.items || [])
        );
    }

    private normalizeGetAllShopsResponse(res: any, page?: number, limit?: number): { items: any[]; meta: any } {
        const items = Array.isArray(res) ? res : (res?.items || []);
        const meta = Array.isArray(res)
            ? {
                currentPage: page || 1,
                itemsPerPage: limit || items.length || 20,
                totalItems: items.length,
                totalPages: 1,
            }
            : (res?.meta || {
                currentPage: page || 1,
                itemsPerPage: limit || 20,
                totalItems: items.length,
                totalPages: 1,
            });
        return { items, meta };
    }

    reorder(id, body): Observable<any> {
        return this.request({
            path: `sale-orders/reorder/${id}`,
            method: 'PUT',
            body
        });
    }
    // test
    deleteMultipleOrder(body): Observable<any> {
        return this.request({
            path: `sale-orders/duplicates`,
            method: 'DELETE',
            body: body
        });
    }

    regenrateOrdersInvoice(ids: number[]): Observable<any> {
        return this.request({
            path: `sale-orders/regenerate-sale-orders-invoice`,
            method: 'POST',
            body: ids
        });
    }
    deleteOrders(ids: number[]): Observable<any> {
        return this.request({
            path: `sale-orders`,
            method: 'DELETE',
            body: ids
        });
    }

    updateOrder(id: number, body): Observable<any> {
        return this.request({
            method: 'PUT',
            path: `sale-orders/${id}`,
            body: body
        });
    }

    // TO DO TEMPORARY SOLUTION FOR PROD NEED TO REMOVE
    updateOrderStripeAmount(id: number, body): Observable<any> {
        return this.request({
            method: 'PUT',
            path: `sale-orders/${id}/stripe-amount`,
        });
    }

    updateOrderValue(id: number, body): Observable<any> {
        return this.request({
            method: 'PUT',
            path: `sale-orders/${id}/sale-order`,
            body: body
        });
    }

    resyncOrderValue(id: number, body): Observable<any> {
        return this.request({
            method: 'PUT',
            path: `sale-orders/${id}/sale-order/resync`,
            body: body
        });
    }

    public downloadInvoice(orderId): any {
        this.request({
            path: `sale-orders/download`,
            method: 'POST',
            body: {
                invoiceId: orderId,
                type: 'PDF'
            }
        }).subscribe(res => {
            // const fileURL = URL.createObjectURL(res);
            window.open(`${res.file}`, '_blank');
        }, error => {
            this.notify.showError('Invoice is not available due to some error');
        });
    }

    getAvailableOrderStates(orderId: number): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders/${orderId}/states`,
        });
    }

    getSaleOrderCheckpoints(orderId: number): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders/aftership-checkpoints/${orderId}`,
        });
    }

    getSaleOrderErrors(page: number, limit: number, q: string = ''): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders/sale-order-errors/error`,
            query: {
                q: q,
                limit: limit,
                page: page,
            }
        });
    }

    getStatusTypes(): Observable<any> {
        return this.request({
            method: 'GET',
            path: 'sale-orders/status-types',
        });
    }

    getSaleOrderStatusHistories(saleOrderId: number): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders/${saleOrderId}/status-histories`,
        });
    }

    appendAdminStatusHistory(body: {
        status_Id?: number;
        saleOrderId: number;
        newStatusId: number;
        status_reason?: string;
    }): Observable<any> {
        return this.request({
            method: 'POST',
            path: 'sale-orders/admin/status-history',
            body,
        });
    }

    getSaleOrderRemarks(saleOrderId: number): Observable<any[]> {
        return this.request({
            method: 'GET',
            path: `sale-orders/${saleOrderId}/remarks`,
        });
    }

    addSaleOrderRemark(saleOrderId: number, text: string): Observable<any> {
        return this.request({
            method: 'POST',
            path: `sale-orders/${saleOrderId}/remarks`,
            body: { text },
        });
    }

    deleteSaleOrderRemark(saleOrderId: number, remarkId: number): Observable<any> {
        return this.request({
            method: 'DELETE',
            path: `sale-orders/${saleOrderId}/remarks/${remarkId}`,
        });
    }

    getRefundErrors(page: number, limit: number): Observable<any> {
        return this.request({
            method: 'GET',
            path: 'sale-orders/refund-errors',
            query: {
                page,
                limit,
            }
        });
    }

    getSaleOrderInvoiceData(id: number, invoiceId: number): Observable<ISaleOrder> {
        return this.request({
            path: `sale-orders/${id}/sale-order-invoice`,
            method: 'POST',
            body: {
                invoiceId: invoiceId
            }
        });
    }

    updateSaleOrderInvoiceData(body: any): Observable<ISaleOrder> {
        return this.request({
            path: `sale-orders/update-sale-order-invoice`,
            method: 'POST',
            body
        });
    }

    updateInvoiceDataWithoutDatabaseChanges(body: any): Observable<ISaleOrder> {
        return this.request({
            path: `sale-orders/update-sale-order-invoice-without-database-changes`,
            method: 'POST',
            body
        });
    }

    //#endregion

    public downloadFile(orderId, id): any {
        this.request({
            path: `sale-orders/${orderId}/documents/${id}`,
            method: 'GET',
        }).subscribe(res => {
            window.open(`${res.file}`, '_blank');
        });
    }

    public deleteOrderDoc(orderId, id): Observable<any> {
        return this.request({
            path: `sale-orders/${orderId}/documents/${id}`,
            method: 'DELETE',
        })
    }

    uploadFile(files: any, orderId, docId, fieldName = 'content') {
        if (files.length > 0) {
            const file: File = files[0];
            const formData: FormData = new FormData();
            formData.append(fieldName, file, file.name);
            const token = this.storageBrowser.get('token');
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', `${environment.api_url}sale-orders/${orderId}/documents/${docId}`, true);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.onload = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        // Use an observer to notify competion
                        this.fileLoadedObserver.next(xhr.responseText);

                    } else {
                        this.fileLoadedObserver.error(xhr.statusText);
                        console.error(xhr.statusText);
                    }
                }
            };
            xhr.onerror = () => {
                this.notify.showError('Something went Wrong!');
                console.error(xhr.statusText);
            };
            xhr.send(formData);
            this.notify.showSuccessMessage('Document Uploaded');
        }
    }

    public updateOrderDoc(orderId, id, body): Observable<any> {
        return this.request({
            path: `sale-orders/${orderId}/documents/${id}`,
            method: 'PUT',
            body: body
        })
    }

    public confirmOrder(orderIds: number[]): Observable<any> {
        return this.request({
            path: `sale-orders/confirm`,
            method: 'POST',
            body: {
                ids: orderIds,
            }
        }).pipe(
            timeout(300000),
            catchError((error) => {
              if (error.name === 'TimeoutError') {
                console.log('The request timed out');
              } else {
                console.log('Error making API request:', error.message);
              }
              return throwError(error);
            })
          );
          // just testing with timeout , need another better solution
    }

    getOrderDocs(orderId): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders/${orderId}/documents`,
        });
    }

    public downloadSaleOrders(
        page: number = 1,
        limit: number = 9999999,
        q: string = '',
        companyId?: number,
        searchParams?: any,
        orderField: string = '',
        orderDirection: string = '',
        forPayouts: boolean = false,
        isB2B: string = SaleOrderType.All.toString(),
        settingsType: string = null,
        placeStatus: PlaceStatus = PlaceStatus.Confirmed,
        isIndVat: boolean = null,
        newFilters: {
            originCountry?: string;
            destinationCountry?: string;
            dateToEmpact?: string;
            dateFromEmpact?: string;
            amountType?: any;
            ShopId?: any;
            customerCountry?: any;
            deliveryDateTo?: any;
            deliveryDateFrom?: any;
        } = {},
        isEmpact: boolean = false,
        exportColumns?: Array<{ key: string; header: string }>,
    ): Observable<any> {
        const query = {
            ...newFilters,
            q: q,
            limit: 9999999,
            page: page,
            companyId: companyId ? companyId : '',
            marketplaces: searchParams?.marketplaces,
            customerTypes: searchParams?.companyTypes,
            dateTo: searchParams?.dateTo,
            dateFrom: searchParams?.dateFrom,
            orderField: orderField,
            orderDirection: orderDirection,
            statuses: searchParams?.orderStatus,
            operationalStatuses: searchParams?.operationalStatus,
            financialStatuses: searchParams?.financialStatus,
            forPayouts: forPayouts ? forPayouts : false,
            isB2B: isB2B ? isB2B : SaleOrderType.All.toString(),
            placeStatus: placeStatus,
            isIndVat,
            isEmpact: isEmpact || "",
        };
        if (settingsType) {
            query['settingsType'] = settingsType;
        }
        return this.requestFile({
            path: `sale-orders/download-sale-orders`,
            method: 'POST',
            query: query,
            body: exportColumns?.length ? { exportColumns } : {},
        }, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet').pipe(map(res => {
            const a = document.createElement('a')
            const objectUrl = URL.createObjectURL(res)
            a.href = objectUrl
            a.download = `Sale-Orders${new Date().toDateString()}.xlsx`;
            a.click();
            URL.revokeObjectURL(objectUrl);
        }));
    }

    public exportSelectedToExcel(
        queryParams: any,
        itemIds: number[],
        exportColumns?: Array<{ key: string; header: string }>,
    ): Observable<any> {
        return this.requestFile({
            path: `sale-orders/export-selected`,
            method: 'POST',
            query: queryParams,
            body: {
                itemIds,
                ...(exportColumns?.length ? { exportColumns } : {}),
            }
        }, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet').pipe(map(res => {
            console.log("RESULT", res);
            const a = document.createElement('a')
            const objectUrl = URL.createObjectURL(res)
            a.href = objectUrl
            a.download = `Sale-Orders${new Date().toDateString()}.xlsx`;
            a.click();
            URL.revokeObjectURL(objectUrl);
        }));
    }

    public buildSaleOrderExportQuery(
        page: number = 1,
        q: string = '',
        companyId?: number,
        searchParams?: any,
        orderField: string = '',
        orderDirection: string = '',
        forPayouts: boolean = false,
        isB2B: string = SaleOrderType.All.toString(),
        settingsType: string = null,
        // Must NOT default to Confirmed — empty means "all statuses" like the B2C list.
        placeStatus?: PlaceStatus | PlaceStatus[] | string | '',
        isIndVat: boolean = null,
        newFilters: {
            originCountry?: string;
            destinationCountry?: string;
            dateToEmpact?: string;
            dateFromEmpact?: string;
            amountType?: any;
            ShopId?: any;
            customerCountry?: any;
            deliveryDateTo?: any;
            deliveryDateFrom?: any;
            batchCountryOrigin?: any;
            batchCountryDestination?: any;
            batchId?: any;
            batchPartnerId?: any;
            fileId?: any;
        } = {},
        isEmpact: boolean = false,
    ): any {
        // Mirror getOrdersB2CTest query shape so export filters match the list.
        const query: any = {
            ...newFilters,
            q: q,
            limit: 9999999,
            page: page,
            companyId: companyId ? companyId : '',
            batchId: newFilters?.batchId ? newFilters.batchId : '',
            batchPartnerId: newFilters?.batchPartnerId ? newFilters.batchPartnerId : '',
            fileId: newFilters?.fileId ? newFilters.fileId : '',
            marketplaces: searchParams?.marketplaces,
            customerTypes: searchParams?.companyTypes,
            ShopId: newFilters?.ShopId,
            dateTo: searchParams?.dateTo,
            dateFrom: searchParams?.dateFrom,
            amountType: newFilters?.amountType,
            customerCountry: newFilters?.customerCountry ?? '',
            orderField: orderField,
            orderDirection: orderDirection,
            statuses: searchParams?.orderStatus,
            operationalStatuses: searchParams?.operationalStatus,
            financialStatuses: searchParams?.financialStatus,
            forPayouts: forPayouts ? forPayouts : false,
            isB2B: isB2B ? isB2B : SaleOrderType.All.toString(),
            isIndVat: isIndVat ? isIndVat : false,
            isEmpact: isEmpact || '',
        };
        const hasPlaceStatus = Array.isArray(placeStatus)
            ? placeStatus.length > 0
            : placeStatus !== undefined && placeStatus !== null && placeStatus !== '';
        if (hasPlaceStatus) {
            query.placeStatus = placeStatus;
        }
        if (settingsType) {
            query.settingsType = settingsType;
        }
        return query;
    }

    public startExportAsync(
        query: any,
        body: {
            itemIds?: Array<number | string>;
            exportColumns?: Array<{ key: string; header: string }>;
        } = {},
    ): Observable<{ jobId: number; status: string }> {
        return this.request({
            path: 'sale-orders/export-async',
            method: 'POST',
            query: this.saleOrderHttpParams(query as Record<string, unknown>),
            body,
        });
    }

    public getExportJob(jobId: number): Observable<any> {
        return this.request({
            path: `sale-orders/export-jobs/${jobId}`,
            method: 'GET',
        });
    }

    public cancelExportJob(jobId: number): Observable<any> {
        return this.request({
            path: `sale-orders/export-jobs/${jobId}/cancel`,
            method: 'POST',
            body: {},
        });
    }

    recalculateTaxReport(soIds: any[]): Observable<any> {
        return this.request({
            method: 'POST',
            path: `sale-orders/recalculate-tex-rep`,
            body: soIds
        });
    }

    syncWithAfterShip(soIds: any[]): Observable<any> {
        return this.request({
            method: 'POST',
            path: `sale-orders/sync-aftership`,
            body: soIds
        });
    }

    getMultipleSOByIds(soIds: any): Observable<any> {
        return this.request({
            method: 'POST',
            path: `sale-orders/get-mulitple-so-byids`,
            body: {
                ids: soIds
            }
        });
    }

    countNewSO(): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders/new-marketplace-so/count`,
        });
    }

    getVatRateByproductCategoryId(categoryId: Number, countryCode: String): Observable<ISaleOrder> {
        return this.request({
            path: `sale-orders/getVatRate`,
            method: 'POST',
            body: {
                categoryId,
                countryCode
            }
        });
    }
    downloadCheckPoints(id: number): any {
        this.requestFile({
            path: `sale-orders/download-check-points/${id}`,
            method: 'GET',
        }, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet').subscribe(res => {
            const a = document.createElement('a')
            const objectUrl = URL.createObjectURL(res)
            a.href = objectUrl
            a.download = 'saleOrderCheckPoints.xlsx';
            a.click();
            URL.revokeObjectURL(objectUrl);
        });
    }

    getSaleOrderConsols(orderId): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders/${orderId}/consols`,
        });
    }

    getOutboundLogs(id: number): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders/${+id}/outbound-logs`,
        });
    }

    createMovementByAuditLog(id: number): Observable<any> {
        return this.request({
            method: 'POST',
            path: `sale-orders/create-movement-by-logs`,
            body: {
                id
            }
        });
    }

    debugAutoDeliveryFeature(time: number): Observable<any> {
        return this.request({
            method: 'POST',
            path: `debugit/auto-delivery-start`,
            body: {
                time
            }
        });
    }

    public getOrderParcels(orderId: number, filter: { q: string; limit: number; page: number }): Observable<any> {
        return this.request({
            method: 'GET',
            path: `sale-orders/${orderId}/parcel`,
            query: {
                q: filter.q,
                limit: filter.limit,
                page: filter.page,
            },
        });
    }

    public createLabels(saleOrderIds: number[]): Observable<any>{
        console.log(saleOrderIds);
        return this.request({
            path: `labels/create`,
            method: 'POST',
            body: {saleOrderIds},
        })
    }

    public refreshLabels(saleOrderIds: number[]): Observable<any> {
        return this.request({
            path: `labels/refresh-sale-order-labels`,
            method: 'POST',
            body: {saleOrderIds},
        });
    }

    public downloadParcelLabel(saleOrderIds: number[]): void {
        console.log(saleOrderIds);
        this.request({path: `labels/get-sale-order-label-urls`, method: 'POST', body: {saleOrderIds}})
            .subscribe({
                next: (res) => {
                    if (res.data && res.data.length > 0) {
                        const urls: string[] = res.data.map((parcel) => parcel.labelUrl);
                        this.mergeLabelsAndOpen(urls);
                    }
                    console.log(res);
                },
                error: (error) => {
                    this.notify.showError('Label is not available due to error');
                },
            });
    }

    public uploadParcelLabel(parcelId: number, file: File): Observable<any> {
        const formData = new FormData();
        formData.append('content', file, file.name);
        const token = this.storageBrowser.get('token');
        return new Observable((obs) => {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', `${environment.api_url}labels/upload/${parcelId}`, true);
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.onload = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        obs.next(JSON.parse(xhr.responseText));
                        obs.complete();
                    } else {
                        obs.error(xhr.responseText || xhr.statusText);
                    }
                }
            };
            xhr.onerror = () => obs.error(xhr.statusText);
            xhr.send(formData);
        });
    }

    private async mergeLabelsAndOpen(urls: string[]): Promise<void> {
        const mergedPdf = await PDFDocument.create();
        for (const url of urls) {
            const res = await fetch(url);
            const bytes = await res.arrayBuffer();
            const pdf = await PDFDocument.load(bytes);
            const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
            copiedPages.forEach((page) => mergedPdf.addPage(page));
        }
        const mergedBytes = await mergedPdf.save();

        // Open in new tab
        const blob = new Blob([mergedBytes], {type: 'application/pdf'});
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
    }
}